import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from 'utils/baseQuery';
import { METHODS } from 'config';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  job: string;
}

interface AttendanceRecord {
  _id: string;
  user: User;
  date: string;
  checkInTime?: string;
  checkOutTime?: string;
  workDuration?: number; // in minutes
  status: 'present' | 'absent' | 'late' | 'half_day';
  notes?: string;
}

interface TodayAttendance {
  hasCheckedIn: boolean;
  hasCheckedOut: boolean;
  checkInTime?: string;
  checkOutTime?: string;
  workDuration?: number;
}

interface AttendanceReportQuery {
  fromDate?: string;
  toDate?: string;
  userId?: string;
}

interface AttendanceReportResponse {
  data: AttendanceRecord[];
  summary: {
    totalDays: number;
    presentDays: number;
    absentDays: number;
    lateDays: number;
    totalWorkHours: number;
  };
}

interface TeamTodayResponse {
  data: AttendanceRecord[];
  totalEmployees: number;
  checkedIn: number;
  checkedOut: number;
}

const AttendanceApi = createApi({
  reducerPath: 'attendance-api',
  baseQuery: baseQuery(),
  tagTypes: ['Attendance', 'TeamAttendance'],
  endpoints: (builder) => ({
    checkIn: builder.mutation<{ attendance: AttendanceRecord }, { workType: 'office' | 'remote' | 'hybrid'; location?: string; notes?: string }>({
      query: (body) => ({
        url: '/attendance/check-in',
        method: METHODS.post,
        body,
      }),
      invalidatesTags: ['Attendance', 'TeamAttendance'],
    }),
    checkOut: builder.mutation<{ attendance: AttendanceRecord }, void>({
      query: () => ({
        url: '/attendance/check-out',
        method: METHODS.post,
      }),
      invalidatesTags: ['Attendance', 'TeamAttendance'],
    }),
    getTodayAttendance: builder.query<TodayAttendance, void>({
      query: () => ({
        url: '/attendance/today',
        method: METHODS.get,
      }),
      providesTags: ['Attendance'],
    }),
    getAttendanceReport: builder.query<AttendanceReportResponse, AttendanceReportQuery>({
      query: (params) => ({
        url: '/attendance/report',
        method: METHODS.get,
        params,
      }),
      providesTags: ['Attendance'],
    }),
    getTeamTodayAttendance: builder.query<TeamTodayResponse, void>({
      query: () => ({
        url: '/attendance/team/today',
        method: METHODS.get,
      }),
      providesTags: ['TeamAttendance'],
    }),
    getTeamAttendanceReport: builder.query<AttendanceReportResponse, AttendanceReportQuery>({
      query: (params) => ({
        url: '/attendance/team/report',
        method: METHODS.get,
        params,
      }),
      providesTags: ['TeamAttendance'],
    }),
  }),
});

export const {
  useCheckInMutation,
  useCheckOutMutation,
  useGetTodayAttendanceQuery,
  useGetAttendanceReportQuery,
  useGetTeamTodayAttendanceQuery,
  useGetTeamAttendanceReportQuery,
} = AttendanceApi;

export { AttendanceApi };
export type {
  AttendanceRecord,
  TodayAttendance,
  AttendanceReportQuery,
  AttendanceReportResponse,
  TeamTodayResponse,
};
